import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CompassIcon, PenLineIcon, PaletteIcon, PackageCheckIcon, CheckIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
const weeks = [{
  label: 'Week 01',
  days: 'Day 01 — 07',
  title: 'Discover',
  icon: CompassIcon,
  summary: 'Understanding the product, founder story, audience and market.',
  items: ['Discovery session', 'Research and review', 'Initial findings and strategic direction'],
  checkin: 'Findings & direction review'
}, {
  label: 'Week 02',
  days: 'Day 08 — 14',
  title: 'Define',
  icon: PenLineIcon,
  summary: 'Sharpening positioning, story and the strategic decisions that shape the brand.',
  items: ['Positioning and messaging', 'Story refinement', 'Naming and brand direction work'],
  checkin: 'Strategy & messaging sign-off'
}, {
  label: 'Week 03',
  days: 'Day 15 — 21',
  title: 'Design',
  icon: PaletteIcon,
  summary: 'Translating strategy into a premium, usable identity system.',
  items: ['Visual identity exploration', 'Review and refinement'],
  checkin: 'Visual identity review'
}, {
  label: 'Week 04',
  days: 'Day 22 — 28',
  title: 'Deliver',
  icon: PackageCheckIcon,
  summary: 'Organising and handing over the work clearly and professionally.',
  items: ['Final brand assets', 'Brand guidelines', 'Handover'],
  checkin: 'Final handover'
}];
export function WeeklyTimeline() {
  const [active, setActive] = useState(0);
  return <section id="timeline" className="py-20 md:py-32 px-5 md:px-10 bg-paper border-b border-rule/60 relative overflow-hidden" data-id="element-312">
      <div className="max-w-6xl mx-auto relative" data-id="element-313">
        <ScrollReveal data-id="element-314">
          <div className="mb-14 md:mb-20 max-w-3xl" data-id="element-315">
            <p className="text-[11px] uppercase tracking-[0.3em] text-red font-bold mb-6" data-id="element-316">
              — Timeline
            </p>
            <h2 className="text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.04em] font-light mb-6 text-ink" data-id="element-317">
              A focused
              <br data-id="element-318" />
              <span className="font-bold italic" data-id="element-319">4-week</span> process.
            </h2>
            <p className="text-base md:text-lg text-ink-soft leading-relaxed font-light max-w-2xl" data-id="element-320">
              Click a week to explore the focus, deliverables and check-in
              moments. Built for momentum and alignment.
            </p>
          </div>
        </ScrollReveal>

        {/* Horizontal week selector with progress rail */}
        <div className="relative mb-12 md:mb-16" data-id="element-321">
          {/* Rail */}
          <div className="absolute top-7 md:top-8 left-0 right-0 h-[2px] bg-rule" data-id="element-322" />
          <motion.div className="absolute top-7 md:top-8 left-0 h-[2px] bg-red" animate={{
          width: `${(active + 0.5) / weeks.length * 100}%`
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-323" />

          <div className="grid grid-cols-4 gap-2 md:gap-4 relative" data-id="element-324">
            {weeks.map((w, i) => {
            const isActive = active === i;
            const isPast = i < active;
            const Icon = w.icon;
            return <button key={w.label} onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} className="group flex flex-col items-center text-center pt-0 cursor-pointer focus:outline-none" data-id="element-325">
                  {/* Dot/Icon node */}
                  <motion.div className={`relative z-10 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${isActive ? 'bg-red border-red text-ivory' : isPast ? 'bg-ink border-ink text-ivory' : 'bg-paper border-rule text-ink-mute group-hover:border-ink/40'}`} animate={{
                scale: isActive ? 1.05 : 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20
              }} data-id="element-326">
                    <Icon className="w-5 h-5 md:w-6 md:h-6" data-id="element-327" />
                    {isActive && <motion.div className="absolute inset-0 rounded-full border-2 border-red" initial={{
                  scale: 1,
                  opacity: 0.6
                }} animate={{
                  scale: 1.6,
                  opacity: 0
                }} transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} data-id="element-328" />}
                  </motion.div>

                  <p className={`mt-4 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${isActive ? 'text-red' : 'text-ink-mute'}`} data-id="element-329">
                    {w.label}
                  </p>
                  <p className={`mt-1 text-base md:text-2xl font-bold tracking-tight transition-colors duration-300 ${isActive ? 'text-ink' : 'text-ink-mute'}`} data-id="element-330">
                    {w.title}
                  </p>
                  <p className="hidden md:block mt-1 text-[10px] uppercase tracking-[0.2em] text-ink-mute font-medium" data-id="element-331">
                    {w.days}
                  </p>
                </button>;
          })}
          </div>
        </div>

        {/* Detail panel */}
        <div className="bg-ivory/60 border border-rule rounded-sm p-6 md:p-10" data-id="element-332">
          <AnimatePresence mode="wait" data-id="element-333">
            <motion.div key={active} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-334">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10" data-id="element-335">
                <div className="md:col-span-5" data-id="element-336">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-red font-bold mb-3" data-id="element-337">
                    Stage 0{active + 1} of 04 · {weeks[active].days}
                  </p>
                  <h3 className="text-4xl md:text-6xl font-light tracking-[-0.04em] text-ink mb-4" data-id="element-338">
                    <span className="font-bold italic" data-id="element-339">
                      {weeks[active].title}
                    </span>
                  </h3>
                  <p className="text-base md:text-lg text-ink-soft leading-relaxed font-light" data-id="element-340">
                    {weeks[active].summary}
                  </p>
                </div>

                <div className="md:col-span-4" data-id="element-341">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-ink-mute mb-4 font-bold" data-id="element-342">
                    Focus this week
                  </p>
                  <ul className="space-y-3" data-id="element-343">
                    {weeks[active].items.map((item, idx) => <motion.li key={item} initial={{
                    opacity: 0,
                    x: -8
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: idx * 0.08 + 0.1
                  }} className="flex items-start gap-3 text-sm md:text-base text-ink font-medium" data-id="element-344">
                        <CheckIcon className="w-4 h-4 text-red flex-shrink-0 mt-1" data-id="element-345" />
                        {item}
                      </motion.li>)}
                  </ul>
                </div>

                <div className="md:col-span-3" data-id="element-346">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-ink-mute mb-4 font-bold" data-id="element-347">
                    Check-in
                  </p>
                  <div className="bg-ink text-ivory p-5 rounded-sm" data-id="element-348">
                    <div className="w-2 h-2 rounded-full bg-red mb-3 animate-pulse" data-id="element-349" />
                    <p className="text-sm md:text-base font-medium leading-snug" data-id="element-350">
                      {weeks[active].checkin}
                    </p>
                    <p className="text-xs text-ivory/50 mt-2 font-light" data-id="element-351">
                      End of {weeks[active].label.toLowerCase()}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <ScrollReveal delay={0.3} data-id="element-352">
          <div className="mt-10 p-6 md:p-8 border-l-2 border-red bg-paper" data-id="element-353">
            <p className="text-[10px] uppercase tracking-[0.25em] text-red mb-2 font-bold" data-id="element-354">
              Website Sprint
            </p>
            <p className="text-sm md:text-base text-ink-soft font-light leading-relaxed" data-id="element-355">
              The ecommerce website project can begin after approval of the
              brand stage and is subject to scheduling.
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>;
}